import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { take } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import type { OnboardingPrefillMapping } from '../../../features/admin/modules/modules-pages/templates/onboarding-status/onboarding-prefill.types';
import { OwnViewFieldType } from './readonly/own-view-field.type';

/** One selectable field path plus the label an admin recognises it by. */
interface PathOption {
  value: string;
  label: string;
}

/** Module holding the flat root contract that actions write onto an onboarding entry. */
const ONBOARDING_ENTRY_MODULE_CODE = 'onboarding-entry';

/** Module whose entries each hold one onboarding form. */
const ONBOARDING_FORMS_MODULE_CODE = 'onboarding-forms';

/** Structural types that hold no value of their own, so they are never a mapping source or target. */
const EXCLUDED_TYPES = ['pages', 'tabs', 'list', 'wizard', 'accordion', 'title', 'linked-items', 'required-items'];

/**
 * The field picker returns paths relative to the entry (`contacten.primair-contact`), while every
 * other config surface — conditions, action mappings, the seed layer that reads these rules — speaks
 * full keyPaths (`entry.contacten.primair-contact`). Store the full form so a rule reads the same as
 * a path anywhere else, and so a rule copied from another config keeps working.
 */
function toKeyPath(value: string): string {
  return value.startsWith('entry.') ? value : `entry.${value}`;
}

/**
 * Editor for an onboarding form's prefill rules: which value from the onboarding entry's ROOT lands
 * on which of THIS form's own step paths.
 *
 * Why its own type instead of the shared mapping repeater (MappingFieldService): that one sources
 * its target options from `generatedForms[module.id]`, and an onboarding form does not live in a
 * module — it lives in an onboarding-forms ENTRY, under `entry.form.generatedForm`. This component
 * therefore reads the form being designed straight out of the root model, so the target list is
 * always the steps of the form on screen.
 *
 * @see OnboardingPrefillMapping for the stored shape and the seed semantics.
 */
@Component({
  selector: 'formly-field-onboarding-prefill-mapping',
  imports: [MatButtonModule, MatFormFieldModule, MatIconModule, MatSelectModule],
  template: `
    <div class="w-full">
      @if (props.label) {
        <h3 class="text-default font-bold mb-1">{{ props.label }}</h3>
      }
      @if (props.description) {
        <p class="mb-3 text-sm text-accent">{{ props.description }}</p>
      }

      @if (!targetOptions().length) {
        <p class="mb-3 text-sm text-accent" data-cy="prefill-no-form">
          Ontwerp eerst het formulier hieronder; daarna kun je kiezen op welk veld een waarde landt.
        </p>
      }

      @for (row of rows(); track $index) {
        <div class="mb-2 flex flex-wrap items-start gap-2" [attr.data-cy]="'prefill-row-' + $index">
          <mat-form-field class="flex-1 min-w-[220px]" subscriptSizing="dynamic">
            <mat-label>Waarde uit de aanmelding</mat-label>
            <mat-select [value]="row.from" [disabled]="!canMutate()" (valueChange)="setFrom($index, $event)">
              @for (option of sourceOptions(); track option.value) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-icon class="mt-4 shrink-0 text-accent" svgIcon="heroicons_outline:arrow-right"></mat-icon>

          <mat-form-field class="flex-1 min-w-[220px]" subscriptSizing="dynamic">
            <mat-label>Veld in dit formulier</mat-label>
            <mat-select [value]="row.to" [disabled]="!canMutate()" (valueChange)="setTo($index, $event)">
              @for (option of targetOptions(); track option.value) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (canMutate()) {
            <button
              type="button"
              mat-icon-button
              class="mt-2 shrink-0"
              aria-label="Regel verwijderen"
              (click)="removeRow($index)">
              <mat-icon class="text-warn" svgIcon="heroicons_outline:trash"></mat-icon>
            </button>
          }
        </div>
      }

      @if (canMutate()) {
        <button type="button" mat-stroked-button class="small-button" (click)="addRow()" data-cy="prefill-add">
          Regel toevoegen
        </button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyOnboardingPrefillMappingComponent extends OwnViewFieldType<FormlyFieldConfig> implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #formService = inject(FormService);
  readonly #entriesService = inject(EntriesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #route = inject(ActivatedRoute);

  /**
   * Bumped on every edit AND whenever the surrounding form changes.
   *
   * The option lists are derived from the root MODEL, which is a plain object, not a signal: without
   * this the computed would run once — while the entry is still loading and `form.generatedForm` is
   * empty — and cache an empty target list forever.
   */
  readonly #revision = signal(0);

  ngOnInit(): void {
    this.form?.root?.valueChanges
      ?.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#revision.update(value => value + 1));

    const module = this.#modulesStore.getModuleByCode(ONBOARDING_FORMS_MODULE_CODE);
    // From the route, not the model: the entry form's model holds only the entry's OWN fields
    // (`form`, `settings`), never its id.
    const entryId = this.#entryIdFromRoute();
    if (!module?.id || !entryId) {
      return; // a brand-new form has nothing saved to map onto yet
    }
    this.#entriesService
      .getEntry(module.id, entryId)
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: entry => this.#savedForm.set(entry?.entry?.form?.generatedForm),
        error: error => console.error('[prefill-mapping] opgeslagen formulier ophalen mislukt:', error),
      });
  }

  readonly rows = computed<OnboardingPrefillMapping[]>(() => {
    this.#revision();
    const value = this.formControl?.value;
    if (!Array.isArray(value)) {
      return [];
    }
    // Normalise on read so a rule stored before the prefix was settled still selects its option.
    return (value as OnboardingPrefillMapping[]).map(row => ({
      from: row?.from ? toKeyPath(row.from) : '',
      to: row?.to ? toKeyPath(row.to) : '',
    }));
  });

  /**
   * Source options: the root fields of the onboarding-entry module — the flat contract the creating
   * actions write to (`entry.name`, `entry.email`) plus everything else that already sits on the
   * entry, such as the embedded user.
   */
  readonly sourceOptions = computed<PathOption[]>(() => {
    this.#revision();
    const module = this.#modulesStore.getModuleByCode(ONBOARDING_ENTRY_MODULE_CODE);
    const generatedForm = module ? this.#modulesStore.generatedForms()[module.id] : undefined;
    return this.#toOptions(generatedForm);
  });

  /**
   * Target options: the fields of THIS onboarding form, read from the SAVED entry.
   *
   * Deliberately not from the live model. The form builder regenerates `generatedForm` from the
   * source form while the page is open, and that regeneration derives each key from its label — so
   * the live copy says `contacten` is `contactgegevens` and every path picked from it would point at
   * nothing. The wizard renders from the saved form, so that is the only list whose paths are real.
   * Same approach the action editor already uses for "waarde uit een onboarding-formulier"
   * (MappingFieldService.getDynamicFormBuilderOutputFieldConfig).
   *
   * Consequence, and it is the correct one: fields added but not yet saved do not appear here. Save
   * the form first, then map.
   */
  readonly targetOptions = computed<PathOption[]>(() => this.#toOptions(this.#savedForm()));

  /** The saved `entry.form.generatedForm` of the onboarding form being edited. */
  readonly #savedForm = signal<FormlyFieldConfig[] | undefined>(undefined);

  // `canMutate()` (shared on `OwnViewFieldType`, `readonly/own-view-field.type.ts`) is this
  // class's read mode gate.
  //
  // This type is an OWN-VIEW field that is not in `READONLY_KEEP_ENABLED_TYPES`, so read mode
  // stamps `props.readonly` AND `props.disabled` on it (`readonly/readonly.types.ts`). Neither
  // stamp reaches this component on its own: the rows are drawn with standalone `[value]` /
  // `(valueChange)` bindings instead of `formControlName`, so a disabled FormControl leaves both
  // selects live, and the add/remove buttons write through `#write()` regardless. Every mutation
  // path therefore consults this gate — the buttons disappear, the selects grey out, and the four
  // writers below refuse anyway, because a `(valueChange)` can still be raised programmatically.
  addRow(): void {
    if (!this.canMutate()) {
      return;
    }
    this.#write([...this.rows(), { from: '', to: '' }]);
  }

  removeRow(index: number): void {
    if (!this.canMutate()) {
      return;
    }
    this.#write(this.rows().filter((_, i) => i !== index));
  }

  setFrom(index: number, from: string): void {
    if (!this.canMutate()) {
      return;
    }
    this.#write(this.rows().map((row, i) => (i === index ? { ...row, from } : row)));
  }

  setTo(index: number, to: string): void {
    if (!this.canMutate()) {
      return;
    }
    this.#write(this.rows().map((row, i) => (i === index ? { ...row, to } : row)));
  }

  #write(rows: OnboardingPrefillMapping[]): void {
    this.formControl?.setValue(rows);
    this.formControl?.markAsDirty();
    this.#revision.update(value => value + 1);
  }

  /**
   * Id of the entry being edited, taken from the deepest route segment that looks like one
   * (`/onboarding-formulieren/<id>`). Returns undefined while creating a new form.
   */
  #entryIdFromRoute(): string | undefined {
    const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    for (let route = this.#route.snapshot; route; route = route.firstChild as never) {
      for (const value of Object.values(route.params ?? {})) {
        if (typeof value === 'string' && UUID.test(value)) {
          return value;
        }
      }
      if (!route.firstChild) {
        break;
      }
    }
    return undefined;
  }

  #toOptions(generatedForm: FormlyFieldConfig[] | undefined): PathOption[] {
    if (!generatedForm?.length) {
      return [];
    }
    const options = this.#formService.getFieldsOptionsOfModule(
      generatedForm,
      EXCLUDED_TYPES,
      true,
      this.#modulesStore.generatedForms(),
      this.#modulesStore.moduleCodeMapper()
    ) as Array<{ value?: string; label?: string }> | undefined;

    return (options ?? [])
      .filter(option => typeof option?.value === 'string' && option.value !== '')
      .map(option => ({ value: toKeyPath(option.value as string), label: option.label || (option.value as string) }));
  }
}
